import { IconButton, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { MENU_ITEM_GLYPH_SIZE } from '../../ui/iconButtonMetrics';
import { gradientVariantForGlyph } from '../../../theme/iconGradient';
import { safeAnalytics } from '../../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { HELP_LINKS } from '../../../constants/helpLinks';
import { NavRailIcon } from './NavRailIcon';

/**
 * Menu-item glyph, painted in the brand ramp like the rail above it.
 *
 * These are affordance icons rather than entity marks, which normally means
 * flat ink — but they sit in the rail's own footer menu, directly under four
 * gradient rail glyphs, and a flat row there reads as a rendering bug rather
 * than as a category distinction. Every item carries its visible label beside
 * the glyph, so the ramp is never the only thing identifying the control and
 * WCAG 1.4.11 does not bite the way it does on the icon-only trigger.
 */
function HelpGlyph({ name }: { readonly name: string }) {
  return (
    <MaterialIcon
      name={name}
      boxSize={MENU_ITEM_GLYPH_SIZE}
      gradient={gradientVariantForGlyph(name)}
    />
  );
}

/**
 * Where focus goes when the dashboard setup list hides itself from under the
 * keyboard: the menu that holds "Show setup guide", the way back. The id goes
 * on the `Menu`: Chakra replaces any id given to `MenuButton` with its own,
 * `menu-button-<menu id>`.
 */
const HELP_MENU_ID = 'help-menu';
export const HELP_MENU_BUTTON_ID = `menu-button-${HELP_MENU_ID}`;

interface HelpMenuProps {
  /** Open the feedback dialog. Owned by the footer — see {@link HelpMenu}. */
  onOpenFeedback: () => void;
  /**
   * Brings back this workspace's unified setup list after "Hide for now" or
   * "Don't show again". Absent, the item is not shown.
   */
  onShowSetupGuide?: () => void;
}

/**
 * Help cluster in the nav rail footer: what's new, the help centre, and the
 * feedback CTA. Split out of the old sidebar avatar menu, which had grown
 * into an everything-drawer — the account menu now holds identity + workspace,
 * this holds support.
 *
 * The FeedbackDialog itself lives in NavRailFooter rather than here, because
 * the onboarding checklist panel opens the same dialog. One mounted instance
 * with two triggers, not two instances racing one submit.
 *
 * `source: 'sidebar_menu'` on the what's-new event is deliberately unchanged:
 * it is the funnel's only discriminator against the other changelog entry
 * points, and renaming it would fork the series at the move.
 */
export function HelpMenu({ onOpenFeedback, onShowSetupGuide }: HelpMenuProps) {
  return (
    <Menu isLazy placement="right-end" id={HELP_MENU_ID}>
      <MenuButton
        as={IconButton}
        aria-label="Help"
        // `question_mark`, not `help`: Material Symbols' `help` is the glyph
        // WITH a circle around it, and the 2026-09 rail asks for the plain one.
        //
        // Gradient, matching the rail's other glyphs — a DESIGN DECISION taken
        // knowingly, not an oversight. This trigger is icon-only
        // (`aria-label="Help"`, no visible caption), so unlike a NavRailItem it
        // has no label to carry the meaning and the glyph alone identifies the
        // control. The ramp does not clear WCAG 1.4.11's 3:1 in light mode at
        // its outer stops — see theme/__tests__/iconGradientContrast.test.ts
        // for the measurements. Consistency with the rail was chosen over it.
        //
        // If revisited, the fix is a visible caption rather than flat ink: a
        // labelled control makes the gradient safe AND keeps the shading
        // consistent, which is exactly what the rail items already do.
        icon={<NavRailIcon name="question_mark" />}
        variant="ghost"
        size="sm"
      />
      <Portal>
        <MenuList zIndex="toast">
          <MenuItem
            icon={<HelpGlyph name="campaign" />}
            onClick={() => {
              safeAnalytics.track(ANALYTICS_EVENTS.WHATS_NEW_OPENED, {
                source: 'sidebar_menu',
              });
              window.open(HELP_LINKS.CHANGELOG, '_blank', 'noopener,noreferrer');
            }}
          >
            What&apos;s new
          </MenuItem>
          <MenuItem
            icon={<HelpGlyph name="menu_book" />}
            onClick={() => window.open(HELP_LINKS.HOME, '_blank', 'noopener,noreferrer')}
          >
            Help center
          </MenuItem>
          <MenuItem
            icon={<HelpGlyph name="keyboard" />}
            onClick={() =>
              window.open(HELP_LINKS.KEYBOARD_SHORTCUTS, '_blank', 'noopener,noreferrer')
            }
          >
            Keyboard shortcuts
          </MenuItem>
          <MenuItem icon={<HelpGlyph name="feedback" />} onClick={onOpenFeedback}>
            Send feedback
          </MenuItem>
          {onShowSetupGuide && (
            <MenuItem icon={<HelpGlyph name="checklist" />} onClick={onShowSetupGuide}>
              Show setup guide
            </MenuItem>
          )}
        </MenuList>
      </Portal>
    </Menu>
  );
}
